import React, { useState, useEffect } from 'react';

const EditRoomModal = ({ show, onClose, listing, property, onSave }) => {
  const [title, setTitle] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (listing) {
      setTitle(listing.title || '');
    }
  }, [listing]);

  const handleSave = async () => {
    if (!listing?.id) {
      setError('Listing information is missing');
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      const agencyId = sessionStorage.getItem('agencyId');
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      if (!agencyId) {
        throw new Error('No agency ID found in session storage');
      }

      if (!token) {
        throw new Error('No authentication token found');
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/agencies/${agencyId}/properties/${property.id}/rental-listings/${listing.id}`;

      const requestPayload = {
        title: title
      };

      console.log('Updating room with payload:', requestPayload);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update room: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Room updated successfully:', responseData);

      // Call the callback to notify parent component
      if (onSave) {
        onSave(responseData);
      }

      // Close the modal
      handleClose();
    } catch (error) {
      console.error('Error updating room:', error);
      setError(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setError(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h5 className="text-xl font-semibold text-gray-900">Edit Room</h5>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            onClick={handleClose}
            disabled={isUpdating}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="roomTitle" className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              id="roomTitle"
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Room title"
              disabled={isUpdating}
            />
          </div>
        </div>
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            onClick={handleClose}
            disabled={isUpdating}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRoomModal;
